import { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { Paperclip, Mic, ArrowRight, Globe, ChevronDown, Check, Loader2, Trash2, X, Zap, MessageSquare, Github, Play } from 'lucide-react';
import axios from 'axios';
import { GitHubPushModal } from './GitHubPushModal';
import { CodePreviewModal, extractCodeBlocks, canPreviewCode } from './CodePreview';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const ChatInterface = ({ project, onSendMessage, sending, disabled, initialPrompt, onPromptUsed }) => {
  const [prompt, setPrompt] = useState('');
  const [modelId, setModelId] = useState('claude-sonnet');
  const [modelName, setModelName] = useState('Claude 4.5 Sonnet');
  const [showModelDropdown, setShowModelDropdown] = useState(false);
  const [messages, setMessages] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const [showGitHubModal, setShowGitHubModal] = useState(false);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const dropdownRef = useRef(null);
  
  // Check if there's previewable code
  const codeBlocks = extractCodeBlocks(messages);
  const hasPreviewableCode = canPreviewCode(codeBlocks);

  useEffect(() => {
    if (project?.id) fetchConversation();
  }, [project?.id]);

  useEffect(() => {
    if (initialPrompt) {
      setPrompt(initialPrompt);
      if (onPromptUsed) onPromptUsed();
    }
  }, [initialPrompt, onPromptUsed]);

  useEffect(() => {
    const handleClick = (e) => { if (dropdownRef.current && !dropdownRef.current.contains(e.target)) setShowModelDropdown(false); };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const fetchConversation = async () => {
    if (!project?.id) return;
    try {
      const res = await axios.get(`${API}/conversations/${project.id}`);
      setMessages(res.data.messages || []);
    } catch (e) { console.error(e); }
  };

  const clearConversation = async () => {
    if (!project?.id) return;
    try { await axios.delete(`${API}/conversations/${project.id}`); setMessages([]); } catch (e) { console.error(e); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!prompt.trim() || sending || disabled) return;
    const txt = prompt;
    setPrompt('');
    setMessages(prev => [...prev, { role: 'user', content: txt }]);
    try {
      const result = await onSendMessage(txt, modelId);
      if (result?.response) setMessages(prev => [...prev, { role: 'assistant', content: result.response }]);
    } catch (err) { console.error(err); }
  };

  const selectModel = (id, name) => { setModelId(id); setModelName(name); setShowModelDropdown(false); };

  return (
    <div className="flex flex-col">
      {showHistory && messages.length > 0 && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="glass rounded-2xl mb-4 max-h-80 overflow-hidden">
          <div className="flex items-center justify-between px-4 py-3 border-b border-white/10">
            <div className="flex items-center gap-2"><MessageSquare className="w-4 h-4 text-[#FF6B00]" /><span className="text-sm text-white">Historial ({messages.length})</span></div>
            <div className="flex gap-2">
              <button onClick={clearConversation} className="p-1 rounded hover:bg-red-500/20 text-gray-400 hover:text-red-400"><Trash2 className="w-4 h-4" /></button>
              <button onClick={() => setShowHistory(false)} className="p-1 rounded hover:bg-white/10 text-gray-400"><X className="w-4 h-4" /></button>
            </div>
          </div>
          <div className="p-4 max-h-64 overflow-y-auto space-y-3">
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                <div className={`max-w-[80%] p-3 rounded-xl text-sm ${m.role === 'user' ? 'bg-[#FF6B00]/20 text-white' : 'bg-white/5 text-gray-300'}`}>
                  {m.content.slice(0, 400)}{m.content.length > 400 ? '...' : ''}
                </div>
              </div>
            ))}
          </div>
        </motion.div>
      )}

      <form onSubmit={handleSubmit} className="glass rounded-2xl overflow-hidden">
        <div className="p-4 lg:p-6">
          <textarea value={prompt} onChange={(e) => setPrompt(e.target.value)} placeholder={project ? 'Construyeme una aplicación...' : 'Selecciona un proyecto'} rows={3} disabled={disabled || sending} className="w-full bg-transparent text-white text-lg placeholder-gray-500 resize-none focus:outline-none disabled:opacity-50" data-testid="chat-prompt-input" onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSubmit(e); } }} />
        </div>
        <div className="flex items-center justify-between px-4 lg:px-6 pb-4 lg:pb-6">
          <div className="flex items-center gap-2">
            <button type="button" className="p-2 rounded-lg hover:bg-white/5 text-gray-400"><Paperclip className="w-5 h-5" /></button>
            <div className="relative" ref={dropdownRef}>
              <button type="button" onClick={() => setShowModelDropdown(!showModelDropdown)} className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-white/5 border border-white/10" data-testid="model-selector-btn">
                <Zap className="w-4 h-4 text-purple-400" /><span className="text-sm text-gray-300">{modelName}</span><ChevronDown className={`w-3 h-3 text-gray-500 ${showModelDropdown ? 'rotate-180' : ''}`} />
              </button>
              {showModelDropdown && (
                <div className="absolute left-0 bottom-full mb-2 w-56 rounded-xl bg-[#0A0A0A] border border-white/10 shadow-2xl z-50 p-1">
                  <button type="button" onClick={() => selectModel('claude-sonnet', 'Claude 4.5 Sonnet')} className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg ${modelId === 'claude-sonnet' ? 'bg-white/10' : 'hover:bg-white/5'}`}><Zap className="w-4 h-4 text-purple-400" /><span className="text-sm text-white">Claude 4.5 Sonnet</span>{modelId === 'claude-sonnet' && <Check className="w-4 h-4 text-[#FF6B00] ml-auto" />}</button>
                  <button type="button" onClick={() => selectModel('claude-opus', 'Claude 4.5 Opus')} className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg ${modelId === 'claude-opus' ? 'bg-white/10' : 'hover:bg-white/5'}`}><Zap className="w-4 h-4 text-purple-500" /><span className="text-sm text-white">Claude 4.5 Opus</span>{modelId === 'claude-opus' && <Check className="w-4 h-4 text-[#FF6B00] ml-auto" />}</button>
                  <button type="button" onClick={() => selectModel('gpt-5', 'GPT-5.2')} className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg ${modelId === 'gpt-5' ? 'bg-white/10' : 'hover:bg-white/5'}`}><Zap className="w-4 h-4 text-green-400" /><span className="text-sm text-white">GPT-5.2</span>{modelId === 'gpt-5' && <Check className="w-4 h-4 text-[#FF6B00] ml-auto" />}</button>
                  <button type="button" onClick={() => selectModel('gemini-pro', 'Gemini 3 Pro')} className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg ${modelId === 'gemini-pro' ? 'bg-white/10' : 'hover:bg-white/5'}`}><Zap className="w-4 h-4 text-blue-400" /><span className="text-sm text-white">Gemini 3 Pro</span>{modelId === 'gemini-pro' && <Check className="w-4 h-4 text-[#FF6B00] ml-auto" />}</button>
                </div>
              )}
            </div>
            {messages.length > 0 && (
              <button type="button" onClick={() => setShowHistory(!showHistory)} className={`flex items-center gap-1 px-3 py-1.5 rounded-lg border ${showHistory ? 'bg-[#FF6B00]/20 border-[#FF6B00]/50 text-[#FF6B00]' : 'bg-white/5 border-white/10 text-gray-400'}`} data-testid="history-toggle-btn">
                <MessageSquare className="w-4 h-4" /><span className="text-xs">{messages.length}</span>
              </button>
            )}
            {messages.length > 0 && (
              <button type="button" onClick={() => setShowGitHubModal(true)} className="flex items-center gap-1 px-3 py-1.5 rounded-lg bg-white/5 border border-white/10 text-gray-400 hover:text-white hover:border-white/20" data-testid="github-push-btn" title="Subir a GitHub">
                <Github className="w-4 h-4" /><span className="text-xs hidden sm:inline">GitHub</span>
              </button>
            )}
            {hasPreviewableCode && (
              <button type="button" onClick={() => setShowPreviewModal(true)} className="flex items-center gap-1 px-3 py-1.5 rounded-lg bg-green-500/20 border border-green-500/30 text-green-400 hover:bg-green-500/30" data-testid="preview-code-btn" title="Preview en Vivo">
                <Play className="w-4 h-4" /><span className="text-xs hidden sm:inline">Preview</span>
              </button>
            )}
          </div>
          <div className="flex items-center gap-2">
            <div className="flex items-center gap-1 px-3 py-1.5 rounded-lg bg-white/5 border border-white/10"><Globe className="w-4 h-4 text-gray-400" /><span className="text-sm text-gray-300">Público</span></div>
            <button type="button" className="p-2 rounded-lg hover:bg-white/5 text-gray-400" title="Voz"><Mic className="w-5 h-5" /></button>
            <button type="submit" disabled={disabled || sending || !prompt.trim()} className="p-3 rounded-full bg-gray-700 hover:bg-gray-600 text-white disabled:opacity-50" data-testid="send-message-btn">{sending ? <Loader2 className="w-5 h-5 animate-spin" /> : <ArrowRight className="w-5 h-5" />}</button>
          </div>
        </div>
      </form>
      
      {/* GitHub Push Modal */}
      <GitHubPushModal 
        isOpen={showGitHubModal} 
        onClose={() => setShowGitHubModal(false)} 
        project={project}
        conversation={messages}
      />
      
      {/* Code Preview Modal */}
      <CodePreviewModal
        isOpen={showPreviewModal}
        onClose={() => setShowPreviewModal(false)}
        messages={messages}
      />
    </div>
  );
};
